import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const FieldAssignmentModal = ({ fields, recipients, onAssign, onCancel }) => {
  const [assignments, setAssignments] = useState(
    fields.reduce((acc, field) => {
      acc[field.id] = field.props?.assignedTo || '';
      return acc;
    }, {})
  );

  const handleAssignment = (fieldId, recipientEmail) => {
    setAssignments(prev => ({
      ...prev,
      [fieldId]: recipientEmail
    }));
  };

  const handleConfirm = () => {
    // Check if all fields are assigned
    const unassignedFields = fields.filter(field => !assignments[field.id]);
    
    if (unassignedFields.length > 0) {
      toast.error(`Please assign all ${unassignedFields.length} unassigned field(s) to recipients`);
      return;
    }

    onAssign(assignments);
  };

  const getFieldTypeLabel = (field) => {
    const typeIcons = {
      signature: '✍️',
      initials: '📝',
      name: '👤',
      email: '📧',
      text: '📄',
      date: '📅',
      checkbox: '☑️',
      radio: '🔘',
      image: '🖼️'
    };
    
    // Safely get field type - backend returns 'field_type', editor uses 'type'
    const fieldType = field.field_type || field.type || 'unknown';
    const icon = typeIcons[fieldType] || '📋';
    
    // Safely capitalize
    const typeName = fieldType ? fieldType.charAt(0).toUpperCase() + fieldType.slice(1) : 'Field';
    
    // If field has a label, show: "Icon Type - Label"
    // Otherwise show: "Icon Type"
    const label = field.props?.label || field.properties?.label;
    if (label) {
      return `${icon} ${typeName} - "${label}"`;
    }
    
    return `${icon} ${typeName}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-gray-900">Assign Fields to Recipients</h2>
              <p className="text-sm text-gray-600 mt-1">
                You have {recipients.length} recipients. Please assign each field to a specific recipient.
              </p>
              <div className="mt-3 bg-blue-50 border border-blue-200 rounded-md p-3">
                <p className="text-xs text-blue-900">
                  💡 <strong>Tip:</strong> For agreements, place the same field type (e.g., Signature) at different positions on the document, then assign each to a different recipient. Each recipient will only see their assigned fields at their designated positions.
                </p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 transition-colors ml-4"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-4">
            {fields.map((field, index) => {
              const rect = field.rect || field.properties?.rect || {};
              const positionY = rect.y !== undefined ? Math.round(rect.y * 100) : 0;
              
              return (
                <div key={field.id} className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-base font-medium">{getFieldTypeLabel(field)}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-500">
                        <span>Page {field.page || 1}</span>
                        <span>•</span>
                        <span>Position: {positionY}% from top</span>
                      </div>
                    </div>
                    
                    <div className="flex-shrink-0 w-64">
                      <select
                        value={assignments[field.id] || ''}
                        onChange={(e) => handleAssignment(field.id, e.target.value)}
                        className={`w-full px-3 py-2 border rounded-md text-sm ${
                          !assignments[field.id] 
                            ? 'border-red-300 bg-red-50' 
                            : 'border-gray-300'
                        }`}
                      >
                        <option value="">-- Select Recipient --</option>
                        {recipients.map((recipient, idx) => (
                          <option key={idx} value={recipient.email}>
                            {recipient.name} ({recipient.email})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50">
          <div className="text-sm text-gray-600">
            {fields.filter(f => assignments[f.id]).length} of {fields.length} fields assigned
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button onClick={handleConfirm}>
              Confirm & Continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FieldAssignmentModal;
